import { FC, MutableRefObject, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Option } from '@beeline/design-system-react/extracted-styles';

import {
  BuildingSelect,
  BuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import { CollapseSearch } from 'src/components/CollapseSearch/CollapseSearch';
import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import {
  FloorSelect,
  FloorSelectItem,
} from 'src/components/FloorSelect/FloorSelect';
import {
  TownSelect,
  TownSelectItem,
} from 'src/components/TownSelect/TownSelect';
import { getIsMobile } from 'src/store/agent/selectors';
import { getSpacesState } from 'src/store/spaces/selectors';
import { spacesActions } from 'src/store/spaces/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { PlaceType } from 'src/types';

import classes from './SpaceFloorMapSettings.module.scss';

const renderValue = (option: Option<string>): string => option.value ?? '';

const supportedPlaceTypes = new Set([PlaceType.Desk, PlaceType.Parking]);

export interface SpaceFloorMapSettingsProps {
  containerRef: MutableRefObject<HTMLElement | null>;
}

export const SpaceFloorMapSettings: FC<SpaceFloorMapSettingsProps> = ({
  containerRef,
}) => {
  const isMobile = useSelector(getIsMobile);
  const dispatch = useAppDispatch();
  const { town, building, floor, bookingObjects } = useSelector(getSpacesState);

  const { scrollTo } = useFloorMap();

  const options = useMemo((): Option<string>[] => {
    const result: Option<string>[] = [];

    for (const [key, value] of bookingObjects) {
      if (!key || !value.title || !supportedPlaceTypes.has(value.type))
        continue;

      result.push({ id: key, value: value.title });
    }

    return result.sort((a, b) => a.value.localeCompare(b.value));
  }, [bookingObjects]);

  const [value, setValue] = useState<Option<string> | null>(null);
  const [input, setInput] = useState('');

  const filteredOptions = useMemo((): Option<string>[] => {
    const search = input.toLowerCase();

    return options.filter(option =>
      option.value.toLowerCase().includes(search),
    );
  }, [input, options]);

  const handleChangeSearch = useCallback(
    async (option: Option<string>) => {
      setValue(option);

      const found = bookingObjects.get(option.id as string);
      if (found && supportedPlaceTypes.has(found.type)) {
        scrollTo(found);
      }
    },
    [bookingObjects, scrollTo],
  );

  const handleClearSearch = useCallback(() => {
    setValue(null);
  }, []);

  const setTown = useCallback(
    (nextTown: TownSelectItem | undefined) => {
      dispatch(spacesActions.setTown(nextTown));
      handleClearSearch();
      handleClearSearch();
    },
    [dispatch, handleClearSearch],
  );

  const setBuilding = useCallback(
    (nextBuilding: BuildingSelectItem | undefined) => {
      dispatch(spacesActions.setBuilding(nextBuilding));
      handleClearSearch();
    },
    [dispatch, handleClearSearch],
  );

  const setFloor = useCallback(
    (nextFloor: FloorSelectItem | undefined) => {
      dispatch(spacesActions.setFloor(nextFloor));
      handleClearSearch();
    },
    [dispatch, handleClearSearch],
  );

  const tooltipTitle = floor?.value
    ? floor.value.isParking
      ? 'Поиск по номеру парковочного места'
      : 'Поиск по номеру рабочего места'
    : 'Поиск по номеру объекта бронирования';

  return (
    <div className={classes.container}>
      <div className={classes.field}>
        <TownSelect
          town={town}
          onChange={setTown}
          dataTestId='administratorTown'
        />
      </div>
      <div className={classes.field}>
        <BuildingSelect
          building={building}
          onChange={setBuilding}
          townId={town?.value.townId}
          disabled={!town}
          dataTestId='administratorBuilding'
          key={town?.value.townId}
        />
      </div>
      <div className={classes.field}>
        <FloorSelect
          floor={floor}
          onChange={setFloor}
          buildingId={building?.value.buildingId}
          disabled={!building}
          dataTestId='administratorFloor'
          key={building?.value.buildingId}
        />
      </div>
      <CollapseSearch
        placeholder='№ стола'
        type='search'
        size='medium'
        disabled={!options.length}
        options={filteredOptions}
        value={value}
        noOptionsText='не найдено'
        onChange={handleChangeSearch}
        renderValue={renderValue}
        onInputChange={setInput}
        onInputClear={handleClearSearch}
        loading={!options.length}
        fullWidth
        tooltipTitle={tooltipTitle}
        alwaysExpanded={isMobile}
        containerRef={containerRef}
      />
    </div>
  );
};
